import AnimatorConflicts from "../../../../components/Meetings/AnimatorConflicts";
import ApiService from "../../../../services/ApiService";
import translate from "../../../../services/Translate";
import { Animator, LanguageKeys, MeetingTypes, VisibilityKeys } from "./CreationModal.d";

export const visibilityOptions = (language: string): Record<VisibilityKeys, string> => ({
  shared: translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.VISIBILITY.SHARED"),
  private: translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.VISIBILITY.PRIVATE"),
});

export const languageMapping = (language: string): Record<LanguageKeys, string> => ({
  "fr-FR": translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.LANGUAGE.FRENCH"),
  "en-US": translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.LANGUAGE.ENGLISH"),
});

export const getSuggestedNames = (
  meetingType: MeetingTypes,
  selectedProgram: string,
  selectedLanguage: string
): string[] => {
  if (!meetingType?.suggested_names) return [];
  if (selectedLanguage && selectedProgram)
    return meetingType?.suggested_names?.[selectedLanguage]?.[selectedProgram] || [];
  else if (selectedLanguage) {
    if (!meetingType.suggested_names?.[selectedLanguage]) return [];
    return [...Object.values(meetingType.suggested_names?.[selectedLanguage]).flatMap(String)];
  } else
    return [
      ...Object.keys(meetingType.suggested_names)
        .map((language) => Object.values(meetingType.suggested_names[language]).flatMap(String))
        .flat(),
    ];
};

export const getMeetingTypes = (setMeetingTypes: Function, setLoading: Function, language: string) => {
  setLoading((loading: any) => ({ ...loading, types: true }));
  ApiService.get("meetings/types?get_suggested_names=true")
    .then((response) => {
      setMeetingTypes(response.data.meeting_types);
    })
    .catch((err) => {
      console.error("Error fetching meeting types:", err);
      alert(translate(language, "PAGES.MEETINGS.MODAL.ERRORS.FETCH_MEETING_TYPES"));
    })
    .finally(() => setLoading((loading: any) => ({ ...loading, types: true })));
};

export const getProjects = (setProjects: Function, setLoading: Function, language: string) => {
  setLoading((loading: any) => ({ ...loading, projects: true }));
  ApiService.get("projects?only_active=true")
    .then((response) => {
      setProjects(
        response.data.projects.map((project: { id: number; name: string }) => ({
          value: project.id,
          display: project.name,
        }))
      );
    })
    .catch((err) => {
      console.error("Error fetching projects:", err);
      alert(translate(language, "PAGES.MEETINGS.MODAL.ERRORS.FETCH_PROJECTS"));
    })
    .finally(() => setLoading((loading: any) => ({ ...loading, projects: true })));
};

export const getResources = (setAnimators: Function, setPrograms: Function, setLoading: Function, language: string) => {
  setLoading((loading: any) => ({ ...loading, resources: true }));
  ApiService.get("meetings/resources?only_active_animators=true")
    .then((response) => {
      const animatorsWithId = response.data.meeting_staff_and_programs.animators.map(
        (animator: Animator, index: number) => ({
          ...animator,
          id: index,
        })
      );
      setAnimators(animatorsWithId);
      setPrograms(response.data.meeting_staff_and_programs.programs || []);
    })
    .catch((error) => {
      console.error("Failed to get animators and organizers:", error);
      alert(translate(language, "PAGES.MEETINGS.MODAL.ERRORS.GET_ADMINS"));
    })
    .finally(() => setLoading((loading: any) => ({ ...loading, resources: true })));
};

export const createMeeting = (
  payload: any,
  language: string,
  setLoading: Function,
  displayAlert: Function,
  setShowCreationModal: Function,
  resetSelection: Function,
  hideAlert: Function
) => {
  setLoading((loading: any) => ({ ...loading, submit: true }));

  ApiService.post("meetings", payload)
    .then((response) => {
      setShowCreationModal(false);
      const data = response.data;
      const meetingIds = data.meeting_id ? [data.meeting_id] : data.meeting_ids || [];
      if (data.meetings_conflicts) {
        displayAlert({
          type: "warning",
          title: translate(language, "PAGES.MEETINGS.DST_ALERT.WARNING.TITLE"),
          message: translate(language, "PAGES.MEETINGS.DST_ALERT.WARNING.MESSAGE"),
          extraContent: <AnimatorConflicts conflicts={data.meetings_conflicts.animator_conflicts} />,
          clickOutsideToClose: true,
          buttons: [
            {
              label: translate(language, "PAGES.MEETINGS.DST_ALERT.WARNING.OPEN_CONFLICTS"),
              variant: "secondary",
              onClick: () => {
                hideAlert();
                data.meetings_conflicts.animator_conflicts
                  .flatMap((conflict: any) => conflict.conflicting_meetings.map((meeting: any) => meeting.id))
                  .forEach((id: string) => window.open(`/meeting/${id}`, "_blank"));
              },
            },
            {
              label: translate(language, "PAGES.MEETINGS.DST_ALERT.WARNING.OPEN_MEETINGS"),
              variant: "primary",
              onClick: () => {
                hideAlert();
                data.meetings_conflicts.meeting_ids.forEach((id: string) => window.open(`/meeting/${id}`, "_blank"));
              },
            },
          ],
        });
      } else {
        displayAlert({
          type: "success",
          title: translate(language, "PAGES.MEETINGS.DST_ALERT.SUCCESS.TITLE"),
          clickOutsideToClose: true,
          buttons: [
            {
              label: translate(language, "PAGES.MEETINGS.DST_ALERT.SUCCESS.OPEN_MEETINGS"),
              variant: "secondary",
              onClick: () => {
                hideAlert();
                meetingIds.forEach((id: string) => window.open(`/meeting/${id}`, "_blank"));
              },
            },
            {
              label: translate(language, "PAGES.MEETINGS.DST_ALERT.SUCCESS.CREATE_MEETING"),
              variant: "primary",
              onClick: () => {
                hideAlert();
                setShowCreationModal(true);
              },
            },
          ],
        });
      }

      resetSelection();
    })
    .catch((error) => {
      console.error("Error creating meeting:", error);
      setShowCreationModal(false);

      displayAlert({
        type: "danger",
        title: translate(language, "PAGES.MEETINGS.DST_ALERT.ERROR.TITLE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "PAGES.MEETINGS.DST_ALERT.ERROR.TRY_AGAIN"),
            variant: "primary",
            onClick: () => {
              hideAlert();
              setShowCreationModal(true);
            },
          },
        ],
      });
    })
    .finally(() => setLoading((loading: any) => ({ ...loading, submit: false })));
};
